import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// OperatorsPage.jsx
//
// Gestión de Operadores / Choferes (SIN login).
// Pantalla SEPARADA del módulo de Usuarios. La UI deja claro al usuario que
// estos operadores no inician sesión, sólo se asignan a vehículos.
//
// Permisos: managers+ pueden crear/editar/borrar. Resto sólo ve.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Badge as BadgeIcon,
  Info as InfoIcon,
  DirectionsCar as VehicleIcon,
} from '@mui/icons-material';

import { useNotification } from '../components/common/GlobalNotifications';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import OperatorVehiclesPanel from '../components/operators/OperatorVehiclesPanel';
import OperatorContactMenu from '../components/operators/OperatorContactMenu';
import PhoneInput from '../components/common/PhoneInput';

// El rol vive en `currentCompany.role` (no `user.role`). useAuth expone
// `hasPermission('manager')` que ya considera admin/superadmin/manager.

const EMPTY_FORM = {
  name: '',
  last_name: '',
  phone: '',
  email: '',
  license_number: '',
  license_expires_at: '',
  is_active: true,
};

const OperatorsPage = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = useAuth();
  const isManager = hasPermission('manager');

  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // operator object or null (create)
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  // Dialog para gestionar los vehículos asignados a un operador específico.
  const [vehiclesDialog, setVehiclesDialog] = useState({ open: false, operator: null });

  const fetchOperators = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.operators.list({ search, active_only: activeOnly });
      setOperators(resp.data || []);
    } catch (e) {
      showError(t('hardcoded.pages_operatorspage.no_se_pudieron_cargar_los'));
    } finally {
      setLoading(false);
    }
  }, [search, activeOnly, showError]);

  useEffect(() => { fetchOperators(); }, [fetchOperators]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (op) => {
    setEditing(op);
    setForm({
      name: op.name || '',
      last_name: op.last_name || '',
      phone: op.phone || '',
      email: op.email || '',
      license_number: op.license_number || '',
      license_expires_at: op.license_expires_at || '',
      is_active: !!op.is_active,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      showError(t('hardcoded.components_operators_operatorquickcreateform.el_nombre_es_obligatorio'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Empty string → undefined para campos opcionales
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') payload[k] = null;
      });
      if (editing) {
        await api.operators.update(editing.id, payload);
        showSuccess(t('hardcoded.pages_operatorspage.operador_actualizado'));
      } else {
        await api.operators.create(payload);
        showSuccess(t('hardcoded.pages_operatorspage.operador_creado'));
      }
      setDialogOpen(false);
      fetchOperators();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('hardcoded.pages_operatorspage.error_al_guardar_operador');
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.operators.delete(confirmDelete.id);
      showSuccess(t('hardcoded.pages_operatorspage.operador_eliminado'));
      setConfirmDelete(null);
      fetchOperators();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('hardcoded.components_ai_aimemoryeditor.no_se_pudo_eliminar');
      showError(msg);
    }
  };

  const filtered = useMemo(() => operators, [operators]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <BadgeIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700}>
          {t('hardcoded.pages_operatorspage.operadores_choferes')}</Typography>
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        {t('hardcoded.pages_operatorspage.los_operadoreschoferes_registrados_aqui')}<strong>{t('hardcoded.pages_operatorspage.no_tienen_acceso_para_iniciar')}</strong>.
        {t('hardcoded.pages_operatorspage.solo_se_usan_para')}
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            placeholder={t('hardcoded.pages_operatorspage.buscar_por_nombre_documento_telefono')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_operatorspage.solo_activos')}</Typography>
            <Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} size="small" />
          </Stack>
          {isManager && (
            <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
              {t('hardcoded.components_operators_operatorquickcreateform.nuevo_operador')}</Button>
          )}
        </Stack>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('pages_operatorspage.table_name')}</TableCell>
                <TableCell>{t('settings.company.phone')}</TableCell>
                <TableCell>{i18n.t('hardcoded.pages_operatorspage.licencia')}</TableCell>
                <TableCell>{t('vehicles.title')}</TableCell>
                <TableCell>{i18n.t('gps.table.status')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('hardcoded.pages_operatorspage.no_hay_operadores_registrados')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((op) => (
                  <TableRow key={op.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{op.full_name}</Typography>
                      {op.email && (
                        <Typography variant="caption" color="text.secondary">{op.email}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {op.phone
                        ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2">{op.phone}</Typography>
                            <OperatorContactMenu phone={op.phone} phoneE164={op.phone_e164} name={op.full_name} />
                          </Stack>
                        )
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {op.license_number || '—'}
                      {op.license_expires_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {i18n.t('hardcoded.pages_operatorspage.vence')}{op.license_expires_at}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('hardcoded.pages_operatorspage.ver_y_gestionar_vehiculos_asignados')}>
                        <Chip
                          size="small"
                          icon={<VehicleIcon sx={{ fontSize: 14 }} />}
                          label={t('hardcoded.pages_operatorspage.assigned_vehicle_count_asignadoopassigned_vehi', { assigned_vehicle_count: op.assigned_vehicle_count, opassigned_vehi: op.assigned_vehicle_count === 1 ? '' : 's' })}
                          clickable
                          onClick={() => setVehiclesDialog({ open: true, operator: op })}
                          color={op.assigned_vehicle_count > 0 ? 'primary' : 'default'}
                          variant={op.assigned_vehicle_count > 0 ? 'filled' : 'outlined'}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={op.is_active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                        color={op.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('hardcoded.components_operators_operatorvehiclespanel.vehiculos_asignados')}>
                        <IconButton size="small" onClick={() => setVehiclesDialog({ open: true, operator: op })}>
                          <VehicleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isManager && (
                        <>
                          <Tooltip title={t('common.edit')}>
                            <IconButton size="small" onClick={() => openEdit(op)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('audit.actions.DELETE')}>
                            <IconButton size="small" color="error" onClick={() => setConfirmDelete(op)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? t('hardcoded.pages_operatorspage.editar_operador') : t('hardcoded.components_operators_operatorquickcreateform.nuevo_operador')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('hardcoded.pages_operatorspage.este_operador_no_tendra_acceso')}</Alert>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={t('hardcoded.pages_operatorspage.nombre_asterisco')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
              <TextField label={t('createUser.form.lastName')} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} fullWidth />
            </Stack>
            {/* Contacto: pais + telefono en un solo control (bandera y codigo). */}
            <PhoneInput
              label={t('settings.company.phone')}
              value={form.phone}
              onChange={(value) => setForm({ ...form, phone: value })}
            />
            <TextField label={t('hardcoded.pages_operatorspage.email_opcional_no_se_usa')} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />

            {/* Licencia: numero y vencimiento juntos, que es como se consultan. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={t('hardcoded.components_operators_operatorquickcreateform.numero_de_licencia')} value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} fullWidth />
              <TextField
                label={t('hardcoded.pages_operatorspage.vence_licencia')}
                type="date"
                value={form.license_expires_at}
                onChange={(e) => setForm({ ...form, license_expires_at: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">{i18n.t('common.active')}</Typography>
              <Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : (editing ? t('common.save') : t('audit.actions.CREATE'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: gestionar vehículos asignados al operador */}
      <Dialog
        open={vehiclesDialog.open}
        onClose={() => { setVehiclesDialog({ open: false, operator: null }); fetchOperators(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VehicleIcon color="primary" />
            <span>{t('hardcoded.pages_operatorspage.asignaciones_de')}{vehiclesDialog.operator?.full_name || '—'}</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {vehiclesDialog.operator && (
            <OperatorVehiclesPanel
              operator={vehiclesDialog.operator}
              canEdit={isManager}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVehiclesDialog({ open: false, operator: null }); fetchOperators(); }}>
            {t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('hardcoded.pages_operatorspage.eliminar_operador')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('hardcoded.pages_operatorspage.eliminar_a')}<strong>{confirmDelete?.full_name}</strong>{t('hardcoded.pages_operatorspage.se_quitaran_tambien_todas_sus')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">{t('audit.actions.DELETE')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorsPage;
